const DB_NAME = 'agm_one_offline';
const DB_VERSION = 2;
const ENTRY_STORE = 'offline_entries';
const CACHE_STORE = 'api_cache';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(ENTRY_STORE)) {
        db.createObjectStore(ENTRY_STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: 'url' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ==================== OFFLINE ENTRIES ====================

export async function saveOfflineEntry(entry) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ENTRY_STORE, 'readwrite');
    const store = tx.objectStore(ENTRY_STORE);
    const record = { ...entry, created_at: new Date().toISOString(), synced: false };
    const req = store.add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getOfflineEntries() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ENTRY_STORE, 'readonly');
    const store = tx.objectStore(ENTRY_STORE);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result.filter(e => !e.synced));
    req.onerror = () => reject(req.error);
  });
}

export async function markEntrySynced(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ENTRY_STORE, 'readwrite');
    const store = tx.objectStore(ENTRY_STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.synced = true;
        store.put(record);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

export async function clearSyncedEntries() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ENTRY_STORE, 'readwrite');
    const store = tx.objectStore(ENTRY_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      req.result.filter(e => e.synced).forEach(e => store.delete(e.id));
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

export async function clearAllPendingEntries() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ENTRY_STORE, 'readwrite');
    const store = tx.objectStore(ENTRY_STORE);
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Sync offline entries to server using the RAW api (not offline-wrapped)
 * to prevent re-saving on network failures during sync
 */
export async function syncOfflineEntries(rawApi) {
  const entries = await getOfflineEntries();
  if (entries.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const entry of entries) {
    try {
      const { id, created_at, synced: _, ...data } = entry;
      await rawApi.post(`/${data.entry_type}`, data.payload);
      await markEntrySynced(id);
      synced++;
    } catch {
      failed++;
    }
  }

  await clearSyncedEntries();
  return { synced, failed };
}

export function getPendingCount() {
  return getOfflineEntries().then(entries => entries.length).catch(() => 0);
}

// ==================== API RESPONSE CACHE ====================

const CACHEABLE_URLS = [
  '/styles', '/machines', '/karigars', '/cutting-orders',
  '/master-data',
];

export function isCacheableUrl(url) {
  return CACHEABLE_URLS.some(cu => url.includes(cu));
}

export async function cacheApiResponse(url, data) {
  try {
    const db = await openDB();
    const tx = db.transaction(CACHE_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_STORE);
    store.put({ url, data, cached_at: new Date().toISOString() });
  } catch {
    // Silently fail cache write
  }
}

export async function getCachedResponse(url) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      const req = store.get(url);
      req.onsuccess = () => resolve(req.result?.data || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}
